/* ===== game/boss-earth-art.ts — 갱을 메운 것의 흙 · 돌 기술 그림(흙더미에서 튀어 나오는 바위 · 석순 · 굴러오는 흙더미) ===== */
import { TAU } from '../../engine/core/math.js';
import { STRIKE_ART, floorMark, rock } from './boss-hazard-art.js';
import { WALL_ART } from './boss-field-art.js';
/* ★ 기술은 흙더미(보스)에서 나온다(점검판 2026-11-03 "흙더미로 시작하니 스킬도 거기서 나오는 것처럼") — 바위는 하늘이 아니라 둔덕에서 포물선으로 튀고,
   석순은 흰 뼈 기둥이 아니라 켜가 진 돌 원뿔이 흙을 뚫고 오르며 부스러기와 먼지를 뿜는다. 사연: docs/code-history.md#h262 */

type C2 = CanvasRenderingContext2D;
const hs = (s: number, i: number) => { const v = Math.sin(s * 127.1 + i * 311.7) * 43758.5453; return v - Math.floor(v); };

/** 먼지 뭉게 — 갈색 연기 덩이 여럿(u 0→1 퍼지며 옅어진다) */
export function dustPuff(c: C2, sd: number, x: number, y: number, r: number, u: number, n = 6) {
  for (let i = 0; i < n; i++) {
    const a = -Math.PI * (0.05 + 0.9 * hs(sd, i)), d = r * (0.3 + u * (0.8 + hs(sd, i + 5) * 0.6)), rr = r * (0.35 + u * 0.55) * (0.7 + hs(sd, i + 9) * 0.5);
    const px = x + Math.cos(a) * d, py = y + Math.sin(a) * d * 0.6;
    const g = c.createRadialGradient(px - rr * 0.2, py - rr * 0.3, 0, px, py, rr);
    g.addColorStop(0, `rgba(196,170,132,${0.75 * (1 - u)})`); g.addColorStop(0.6, `rgba(150,124,92,${0.5 * (1 - u)})`); g.addColorStop(1, 'rgba(120,98,70,0)');
    c.fillStyle = g; c.beginPath(); c.arc(px, py, rr, 0, TAU); c.fill();
  }
}

/** 석순 — 켜진 돌 원뿔. 윤곽은 울퉁불퉁 · 한쪽은 빛, 한쪽은 그늘 · 밑동에 깨진 흙 */
export function stalagmite(c: C2, sd: number, x: number, y: number, w: number, hgt: number) {
  if (hgt < 2) return;
  const n = 9, L: number[] = [], R: number[] = [], lean = (hs(sd, 1) - 0.5) * w * 0.4;
  for (let i = 0; i <= n; i++) {
    const u = i / n, half = w * 0.5 * (1 - u) ** 0.9 * (0.88 + hs(sd, i + 10) * 0.24), cx = x + lean * u;
    L.push(cx - half - (i % 3 === 1 ? 2 : 0), y - hgt * u); R.push(cx + half + (i % 3 === 2 ? 2 : 0), y - hgt * u);
  }
  const outline = () => { c.beginPath(); c.moveTo(L[0], L[1]); for (let i = 2; i < L.length; i += 2) c.lineTo(L[i], L[i + 1]); for (let i = R.length - 2; i >= 0; i -= 2) c.lineTo(R[i], R[i + 1]); c.closePath(); };
  outline();
  const g = c.createLinearGradient(x - w / 2, 0, x + w / 2, 0);
  g.addColorStop(0, '#4a3e32'); g.addColorStop(0.35, '#8a7a62'); g.addColorStop(0.55, '#a8977a'); g.addColorStop(1, '#5a4c3c');
  c.fillStyle = g; c.fill();
  c.save(); outline(); c.clip();
  for (let i = 1; i < 7; i++) {                                          // 켜 — 물이 쌓은 띠(굵기 · 색이 조금씩 다르다)
    const yy = y - hgt * (i / 7) + (hs(sd, i + 30) - 0.5) * 4;
    c.fillStyle = i % 2 ? 'rgba(60,46,34,.35)' : 'rgba(200,184,150,.25)'; c.fillRect(x - w, yy, w * 2, 2 + hs(sd, i + 40) * 3);
  }
  c.strokeStyle = 'rgba(40,30,22,.45)'; c.lineWidth = 1;                 // 흘러내린 골
  for (let i = 0; i < 3; i++) { const ox = (hs(sd, i + 50) - 0.5) * w * 0.5; c.beginPath(); c.moveTo(x + ox + lean * 0.8, y - hgt * 0.85); c.quadraticCurveTo(x + ox * 1.2 + lean * 0.4, y - hgt * 0.45, x + ox * 1.4, y); c.stroke(); }
  const v = c.createLinearGradient(0, y - hgt, 0, y); v.addColorStop(0, 'rgba(255,240,210,.18)'); v.addColorStop(1, 'rgba(20,14,8,.45)');
  c.fillStyle = v; c.fillRect(x - w, y - hgt, w * 2, hgt);
  c.restore();
  c.strokeStyle = 'rgba(230,214,180,.6)'; c.lineWidth = 1.2;             // 빛 받는 날
  c.beginPath(); c.moveTo(L[0] + 2, L[1]); for (let i = 2; i < L.length; i += 2) c.lineTo(L[i] + 2, L[i + 1]); c.stroke();
  c.strokeStyle = '#241c14'; c.lineWidth = 1.2; outline(); c.stroke();
  for (let i = 0; i < 4; i++) rock(c, sd + i * 2.7, x + (hs(sd, i + 60) - 0.5) * w * 1.5, y - 2 - hs(sd, i + 64) * 3, 2.5 + hs(sd, i + 68) * 3.5, hs(sd, i));   // 밑동 깨진 흙
}

/** 흙더미 — 굴러오는 둔덕(흙 · 돌 · 뿌리) */
function dirtHeap(c: C2, sd: number, x: number, top: number, bot: number, w: number, dir: number, T: number) {
  const H = bot - top, n = 12;
  c.beginPath(); c.moveTo(x - w * 0.9, bot);
  for (let i = 0; i <= n; i++) {
    const u = i / n, a = Math.PI * (1 - u), bump = 1 + (hs(sd, i + Math.floor(T * 6) % 3) - 0.5) * 0.12;
    c.lineTo(x + Math.cos(a) * w * (u > 0.5 ? 0.75 : 0.9), bot - Math.sin(a) ** 0.8 * H * bump);
  }
  c.closePath();
  const g = c.createLinearGradient(0, top, 0, bot); g.addColorStop(0, '#8a6a46'); g.addColorStop(0.5, '#5e4630'); g.addColorStop(1, '#3a2a1c');
  c.fillStyle = g; c.fill(); c.strokeStyle = '#21170e'; c.lineWidth = 1.4; c.stroke();
  for (let i = 0; i < 7; i++) {                                          // 박힌 돌(구르며 돈다)
    const a = (hs(sd, i) * TAU - T * dir * 5) % TAU, rr = w * (0.25 + hs(sd, i + 7) * 0.45);
    const px = x + Math.cos(a) * rr * 0.8, py = bot - H * 0.45 + Math.sin(a) * H * 0.35;
    if (py < bot - 3 && py > top + 3) rock(c, sd + i, px, py, 3 + hs(sd, i + 14) * 4, a);
  }
  c.strokeStyle = '#3a2614'; c.lineWidth = 1.5;                          // 뿌리
  for (let i = 0; i < 3; i++) { const px = x + (hs(sd, i + 20) - 0.5) * w; c.beginPath(); c.moveTo(px, top + H * 0.3); c.quadraticCurveTo(px + 8, top + H * 0.1, px + 4 - dir * 10, top - 4); c.stroke(); }
}

Object.assign(STRIKE_ART, {
  /** 흙더미에서 튀는 바위 — 둔덕이 터지며 포물선으로 날아와 박힌다 */
  ejecta: {
    tell(c: C2, h: Bag, x: number, y: number, k: number, T: number) {
      floorMark(c, x, y, h.r * (0.3 + k * 0.7), '#1a140e', 0.2 + k * 0.45);
      if (h.sx === undefined) return;
      const ox = h.sx - h.x + x, oy = h.sy - h.y + y, launch = Math.min(1, k / 0.18);
      if (k < 0.35) dustPuff(c, h.sd, ox, oy + 10, 26, k / 0.35, 5);    // 둔덕이 터진다
      if (k < 0.18) return;
      const u = (k - 0.18) / 0.82, px = ox + (x - ox) * u, py = oy + (y - 10 - oy) * u - Math.sin(u * Math.PI) * 200;
      for (let i = 1; i <= 4; i++) {                                     // 흙 꼬리
        const v = Math.max(0, u - i * 0.03), qx = ox + (x - ox) * v, qy = oy + (y - 10 - oy) * v - Math.sin(v * Math.PI) * 200;
        c.fillStyle = `rgba(150,124,92,${0.5 - i * 0.1})`; c.beginPath(); c.arc(qx, qy, 6 - i, 0, TAU); c.fill();
      }
      rock(c, h.sd, px, py, Math.max(8, h.r * 0.36) * launch, T * 8);
    },
    hit(c: C2, h: Bag, x: number, y: number, f: number) { dustPuff(c, h.sd, x, y - 4, h.r * 0.9, f); STRIKE_ART.rock.hit(c, h, x, y, f, 0); }
  },
  /** 석순 — 땅에 금이 가고 먼지가 새다가 돌 원뿔이 뚫고 오른다 */
  stalag: {
    tell(c: C2, h: Bag, x: number, y: number, k: number, T: number) {
      floorMark(c, x, y, h.w * 0.5, '#1a140e', 0.25 + k * 0.4);
      c.fillStyle = 'rgba(160,136,104,.85)';
      for (let i = 0; i < 6; i++) { const u = (T * 2.4 + hs(h.sd, i)) % 1; c.fillRect(x + (hs(h.sd, i + 3) - 0.5) * h.w, y - u * 14 * k, 2, 2); }
      if (k > 0.6) stalagmite(c, h.sd, x, y, h.w * 0.5, (k - 0.6) * 18);
    },
    hit(c: C2, h: Bag, x: number, y: number, f: number) {
      const up = Math.min(1, f * 6), e = up < 1 ? 1 - (1 - up) ** 3 * (1 + 2 * up) : 1, end = Math.max(0, f - 0.75) / 0.25;
      c.globalAlpha = 1 - end * end;
      stalagmite(c, h.sd, x, y + end * h.hh * 0.3, h.w * 1.05, h.hh * e * (0.8 + hs(h.sd, 7) * 0.3) * (1 - end * 0.3));
      c.globalAlpha = 1;
      if (f < 0.5) dustPuff(c, h.sd, x, y - 2, h.w * 0.9, f / 0.5, 7);
      for (let i = 0; i < 6 && f < 0.45; i++) {                         // 튀는 흙덩이
        const u = f / 0.45, a = -Math.PI * (0.15 + 0.7 * hs(h.sd, i + 20)), d = u * (30 + hs(h.sd, i + 25) * 40);
        rock(c, h.sd + i, x + Math.cos(a) * d, y - 6 + Math.sin(a) * d + u * u * 50, 2.5 + hs(h.sd, i + 30) * 2.5, u * 8);
      }
    }
  }
});

Object.assign(WALL_ART, {
  dirt(c: C2, h: Bag, x: number, top: number, bot: number, f: number, T: number) {   // 흙더미 밀기 — 굴러오는 둔덕 · 앞으로 쏟아지는 흙 · 뒤로 이는 먼지
    const dir = Math.sign(h.vx) || 1;
    c.globalAlpha = f;
    dustPuff(c, h.sd, x - dir * h.w, bot - 6, (bot - top) * 0.6, (T * 1.5) % 1, 5);
    dirtHeap(c, h.sd, x, top, bot, h.w * 1.6, dir, T);
    for (let i = 0; i < 8; i++) { const u = (T * 2.2 + hs(h.sd, i)) % 1; rock(c, h.sd + i, x + dir * (h.w * 1.1 + u * 30), bot - (bot - top) * 0.6 * (1 - u) ** 2 - 3, 2 + hs(h.sd, i + 4) * 2.5, u * 9); }
    c.globalAlpha = 1;
  }
});
